<template>
    <v-dialog v-model="dialog" width="680" persistent scrollable>
        <v-card>
            <v-card-title class="text-subtitle-1">
                {{ form.id ? 'Editar servidor' : 'Novo servidor' }}
                <v-spacer/>
                <v-btn icon @click="fechar"><v-icon>mdi-close</v-icon></v-btn>
            </v-card-title>

            <v-divider/>

            <v-card-text class="pt-4">
                <v-form ref="form">
                    <v-row dense>
                        <v-col cols="12" sm="7">
                            <v-text-field
                                v-model="form.nome"
                                label="Nome"
                                outlined
                                dense
                                :disabled="!podeEditar"
                                hint="Como aparece na tela e no assunto do e-mail"
                                persistent-hint
                                :rules="[v => !!v || 'Informe um nome']"
                            />
                        </v-col>
                        <v-col cols="12" sm="5">
                            <v-select
                                v-model="form.papel"
                                :items="papeis"
                                label="Papel"
                                outlined
                                dense
                                :disabled="!podeEditar"
                            />
                        </v-col>
                    </v-row>

                    <v-text-field
                        v-model="form.host"
                        label="Host (identificador)"
                        outlined
                        dense
                        class="mt-2"
                        :disabled="!podeEditar || !!form.id"
                        :hint="form.id
                            ? 'O host não muda depois de criado: ele é a chave do histórico já gravado. Para renomear, use o campo Nome.'
                            : 'Letras, números, ponto, hífen e sublinhado. Vai no MONITOR_HOST ou no token do agente.'"
                        persistent-hint
                        :rules="[
                            v => !!v || 'Informe o host',
                            v => /^[A-Za-z0-9._-]+$/.test(v || '') || 'Use apenas letras, números, . _ -',
                        ]"
                    />

                    <v-row dense class="mt-2">
                        <v-col cols="6" sm="3">
                            <v-text-field
                                v-model.number="form.ordem"
                                label="Ordem"
                                type="number"
                                outlined
                                dense
                                :disabled="!podeEditar"
                            />
                        </v-col>
                        <v-col cols="6" sm="4" class="d-flex align-center">
                            <v-switch
                                v-model="form.ativo"
                                label="Ativo"
                                dense
                                inset
                                hide-details
                                class="mt-0 pt-0"
                                :disabled="!podeEditar"
                            />
                        </v-col>
                        <v-col cols="12" sm="5" class="d-flex align-center">
                            <v-switch
                                v-model="form.coleta_local"
                                label="Coleta local"
                                dense
                                inset
                                hide-details
                                class="mt-0 pt-0"
                                :disabled="!podeEditar"
                            />
                        </v-col>
                    </v-row>

                    <div class="text-caption text--secondary mb-4">
                        <strong>Coleta local</strong> é o servidor onde o próprio Laravel roda: ele se
                        coleta pelo schedule e é o único que reporta fila e Postgres. Os demais são
                        alimentados pelo agente.
                    </div>

                    <v-divider class="mb-4"/>

                    <v-combobox
                        v-model="form.config.mountpoints"
                        label="Pontos de montagem"
                        outlined
                        dense
                        multiple
                        small-chips
                        deletable-chips
                        append-icon=""
                        :disabled="!podeEditar"
                        hint="O primeiro vai para os gráficos de disco; os demais ficam no detalhe"
                        persistent-hint
                    />

                    <v-combobox
                        v-model="form.config.servicos"
                        label="Serviços conferidos"
                        outlined
                        dense
                        multiple
                        small-chips
                        deletable-chips
                        append-icon=""
                        class="mt-4"
                        :disabled="!podeEditar"
                        hint="Nomes do systemctl (nginx, docker, php7.4-fpm). Vazio = não confere"
                        persistent-hint
                    />

                    <v-row dense class="mt-4">
                        <v-col cols="12" sm="6">
                            <v-text-field
                                v-model="form.config.interface_rede"
                                label="Interface de rede"
                                outlined
                                dense
                                :disabled="!podeEditar"
                                hint="Vazio = detecta sozinho"
                                persistent-hint
                            />
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-text-field
                                v-model="form.config.device_disco"
                                label="Device de disco"
                                outlined
                                dense
                                :disabled="!podeEditar"
                                hint="Vazio = detecta sozinho"
                                persistent-hint
                            />
                        </v-col>
                    </v-row>

                    <v-row dense class="mt-4">
                        <v-col cols="12" sm="6">
                            <v-text-field
                                v-model="form.config.fpm_status_url"
                                label="URL de status do PHP-FPM"
                                outlined
                                dense
                                :disabled="!podeEditar"
                            />
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-text-field
                                v-model="form.config.nginx_status_url"
                                label="URL de status do Nginx"
                                outlined
                                dense
                                :disabled="!podeEditar"
                            />
                        </v-col>
                    </v-row>

                    <v-divider class="my-4"/>

                    <v-row dense>
                        <v-col cols="12" sm="6">
                            <v-text-field
                                v-model.number="form.heartbeat_minutos"
                                label="Avisar sem coleta após"
                                type="number"
                                outlined
                                dense
                                suffix="min"
                                :disabled="!podeEditar"
                            />
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-text-field
                                v-model.number="form.heartbeat_cooldown_minutos"
                                label="Repetir o aviso no máximo a cada"
                                type="number"
                                outlined
                                dense
                                suffix="min"
                                :disabled="!podeEditar"
                            />
                        </v-col>
                    </v-row>

                    <v-combobox
                        v-model="form.destinatarios_coleta_parada"
                        label="Destinatários do aviso de coleta parada"
                        outlined
                        dense
                        multiple
                        small-chips
                        deletable-chips
                        append-icon=""
                        class="mt-2"
                        :disabled="!podeEditar"
                        hint="Vazio = usa o destinatário padrão do sistema"
                        persistent-hint
                    />

                    <v-textarea
                        v-model="form.observacao"
                        label="Observação"
                        outlined
                        dense
                        rows="2"
                        class="mt-4"
                        :disabled="!podeEditar"
                    />

                    <!-- ── Token do agente ─────────────────────────────── -->
                    <template v-if="form.id && !form.coleta_local">
                        <v-divider class="my-4"/>

                        <div class="text-subtitle-2 mb-1">Agente</div>

                        <div v-if="token" class="token-novo">
                            <div class="text-caption font-weight-medium mb-1">
                                Copie agora — este token não é exibido de novo.
                            </div>
                            <code class="token">{{ token }}</code>

                            <div class="text-caption mt-3">Instalação no <strong>host</strong> da máquina:</div>
                            <pre class="instrucoes">{{ instrucoes }}</pre>
                        </div>

                        <div v-else class="text-caption text--secondary mb-2">
                            <span v-if="form.tem_token">
                                Este servidor já tem um token. Gerar um novo invalida o anterior na hora.
                            </span>
                            <span v-else>
                                Ainda sem token: o agente não consegue enviar métricas.
                            </span>
                        </div>

                        <v-btn v-if="podeEditar" small outlined :loading="gerando" @click="gerarToken">
                            <v-icon left small>mdi-key-variant</v-icon>
                            {{ form.tem_token ? 'Gerar novo token' : 'Gerar token' }}
                        </v-btn>
                    </template>
                </v-form>
            </v-card-text>

            <v-divider/>

            <v-card-actions>
                <v-btn
                    v-if="podeEditar && form.id"
                    text
                    small
                    color="error"
                    @click="remover"
                >
                    <v-icon left small>mdi-delete</v-icon>
                    Excluir
                </v-btn>
                <v-spacer/>
                <v-btn text @click="fechar">{{ podeEditar ? 'Cancelar' : 'Fechar' }}</v-btn>
                <v-btn v-if="podeEditar" color="primary" :loading="salvando" @click="salvar">Salvar</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { PAPEIS } from './servidores';

const VAZIO = {
    id: null,
    host: '',
    nome: '',
    papel: 'outro',
    ordem: 0,
    ativo: true,
    coleta_local: false,
    tem_token: false,
    config: {
        mountpoints: ['/'],
        servicos: [],
        interface_rede: '',
        device_disco: '',
        fpm_status_url: '',
        nginx_status_url: '',
    },
    heartbeat_minutos: 10,
    heartbeat_cooldown_minutos: 60,
    destinatarios_coleta_parada: [],
    observacao: '',
};

export default {
    name: 'ServidorDialog',

    data: () => ({
        dialog: false,
        salvando: false,
        gerando: false,
        token: '',
        papeis: PAPEIS,
        form: JSON.parse(JSON.stringify(VAZIO)),
    }),

    computed: {
        podeEditar() {
            return this.checkPermission('zapercontrol_monitoramento_servidores') || this.isDiretor();
        },

        instrucoes() {
            const url = `${this.$http.defaults.baseURL || 'https://webservice.zapermenu.com.br/api'}`.replace(/\/$/, '');

            return [
                '# 1. copie o monitor-agent.php para /opt/zaper-monitor/ (no HOST, não em container)',
                '# 2. crie /opt/zaper-monitor/monitor-agent.env:',
                `API_URL=${url}`,
                `TOKEN=${this.token}`,
                '',
                '# 3. confira antes de ligar o cron:',
                'php /opt/zaper-monitor/monitor-agent.php --dry-run',
                '',
                '# 4. no crontab:',
                '* * * * * /usr/bin/php /opt/zaper-monitor/monitor-agent.php >/dev/null 2>&1',
            ].join('\n');
        },
    },

    methods: {
        abrir(servidor) {
            this.token = '';

            // Cópia profunda: `config` é objeto aninhado, e um spread raso faria a
            // edição escrever direto na linha que o painel está mostrando — o card
            // mudaria antes de salvar, e continuaria mudado se o salvar falhasse.
            this.form = servidor
                ? {
                    ...JSON.parse(JSON.stringify(VAZIO)),
                    ...JSON.parse(JSON.stringify(servidor)),
                    config: {
                        ...VAZIO.config,
                        ...(servidor.config || {}),
                    },
                    destinatarios_coleta_parada: [...(servidor.destinatarios_coleta_parada || [])],
                }
                : JSON.parse(JSON.stringify(VAZIO));

            this.dialog = true;
        },

        fechar() {
            this.dialog = false;
            this.token = '';
            if (this.$refs.form) this.$refs.form.resetValidation();
        },

        async salvar() {
            if (!this.$refs.form.validate()) return;

            this.salvando = true;
            try {
                const corpo = { ...this.form };
                delete corpo.tem_token;

                if (corpo.id) {
                    await this.$http.put(`monitoramento/servidores/${corpo.id}`, corpo);
                } else {
                    await this.$http.post('monitoramento/servidores', corpo);
                }

                this.notify('Servidor salvo');
                this.$emit('salvo');
                this.fechar();
            } catch (e) {
                // O interceptor do axios já mostrou o motivo.
            }
            this.salvando = false;
        },

        async gerarToken() {
            const ok = await this.$root.$confirm(
                'Gerar token',
                this.form.tem_token
                    ? 'O token atual deixa de funcionar imediatamente, e o agente que estiver usando ele '
                      + 'para de enviar métricas até ser reconfigurado. Confirma?'
                    : 'O token será exibido uma única vez. Confirma?',
            );

            if (!ok) return;

            this.gerando = true;
            try {
                const { data } = await this.$http.post(`monitoramento/servidores/${this.form.id}/token`);

                this.token = data.token;
                this.form.tem_token = true;
                this.$emit('salvo');
            } catch (e) {
                // idem
            }
            this.gerando = false;
        },

        async remover() {
            const ok = await this.$root.$confirm(
                'Excluir servidor',
                `"${this.form.nome}" some da tela e deixa de ser vigiado — inclusive o aviso de coleta parada. `
                + 'O histórico de métricas NÃO é apagado: recriar o servidor com o mesmo host o traz de volta. '
                + 'Se a ideia é só parar de acompanhar por enquanto, desmarque "Ativo" em vez de excluir. Confirma?',
            );

            if (!ok) return;

            try {
                await this.$http.delete(`monitoramento/servidores/${this.form.id}`);
                this.notify('Servidor excluído');
                this.$emit('salvo');
                this.fechar();
            } catch (e) {
                // idem
            }
        },
    },
};
</script>

<style scoped>
.token-novo {
    background: rgba(255, 193, 7, 0.12);
    border-left: 4px solid #ffb300;
    padding: 12px 14px;
    margin-bottom: 12px;
}

.token {
    display: block;
    word-break: break-all;
    font-size: 13px;
    background: rgba(0, 0, 0, 0.06);
    padding: 8px;
    border-radius: 4px;
}

.instrucoes {
    font-size: 11px;
    line-height: 1.5;
    background: rgba(0, 0, 0, 0.06);
    padding: 8px;
    border-radius: 4px;
    overflow-x: auto;
    white-space: pre;
    margin: 4px 0 0;
}
</style>
